import { forwardRef, PropsWithoutRef, useImperativeHandle } from 'react';
import { createZodFormResolver, DateTimeUtils, lt, useTimezone, useFormControls } from '@mews/framework';
import { DateField } from '@mews-commander/form';
import { TextKey } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';
import { useCommanderSelector } from '@mews-commander/utils';
import { PaymentFormProps } from '../types';
import { useDefaultFormContext } from '../useDefaultFormValues';
import { useInvalidatePreauthorizationTableRecordQuery } from '../../../../queries';
import { MewsTerminalFormField } from '../MewsTerminalPayment/schema';
import preferences from '../../../../../preferences';
import { MewsTerminalFormFields } from '../MewsTerminalPayment/MewsTerminalFormFields';
import { useGetPreauthorizationDateTimesUtc } from '../hooks';
import { useAddTerminalPreauthorization } from '../queries';
import {
    createMewsTerminalPreauthorizationFormSchema,
    MewsTerminalPreauthorizationFormField,
    MewsTerminalPreauthorizationFormValues,
} from './schema';

export const MewsTerminalPreauthorizationForm = forwardRef(
    ({ accountId, onSuccess, onSubmitting, onSubmitted }: PropsWithoutRef<PaymentFormProps>, ref) => {
        const timezone = useTimezone();
        const { getPreferredTerminalId } = preferences.selectors;
        const preferredTerminalId = useCommanderSelector(getPreferredTerminalId);
        const invalidatePreauthorizationTableRecordQuery = useInvalidatePreauthorizationTableRecordQuery();
        const addTerminalPreauthorization = useAddTerminalPreauthorization({ accountId });

        const { defaultFormValues } = useDefaultFormContext();
        const resolver = createZodFormResolver<MewsTerminalPreauthorizationFormValues>(
            createMewsTerminalPreauthorizationFormSchema
        );

        const { defaultDateTimeUtc, maxDateTimeUtc, minDateTimeUtc } = useGetPreauthorizationDateTimesUtc();

        const { renderFormContent, submit, hasDirtyFields } = useFormControls<MewsTerminalPreauthorizationFormValues>({
            onSubmit: async (formValues) => {
                try {
                    onSubmitting?.();

                    await addTerminalPreauthorization(formValues);

                    invalidatePreauthorizationTableRecordQuery({ accountId });

                    onSuccess?.();
                } finally {
                    onSubmitted?.();
                }
            },
            defaultValues: {
                ...defaultFormValues,
                [MewsTerminalFormField.PaymentTerminalId]: preferredTerminalId ?? null,
                [MewsTerminalPreauthorizationFormField.ExpirationUtc]: defaultDateTimeUtc.value,
            },
            resolver,
        });

        useImperativeHandle(
            ref,
            () => ({
                submit,
                hasDirtyFields,
            }),
            [submit, hasDirtyFields]
        );

        return renderFormContent(
            <Stack spacing="300" vertical>
                <MewsTerminalFormFields hideLinkPaymentToPlan />
                <DateField
                    required
                    name={MewsTerminalPreauthorizationFormField.ExpirationUtc}
                    label={lt(TextKey.ProcessPaymentMewsPreauthorizationExpirationLabel)}
                    mode="utc"
                    minDate={DateTimeUtils.utcToStructuredDate(minDateTimeUtc.value, timezone)}
                    maxDate={DateTimeUtils.utcToStructuredDate(maxDateTimeUtc.value, timezone)}
                />
            </Stack>
        );
    }
);
